import Constants from "../../../Constants.json";

const VALID_STATUS = new Set(["yes", "watch", "no"]);
const VALID_TONE = new Set(["pos", "warn", "neg", "neutral"]);
const VALID_SIGNAL = new Set(["BUY", "ACCUMULATE", "HOLD", "REDUCE", "AVOID"]);

const num = (v, fallback = 0) => {
  const n = typeof v === "string" ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : fallback;
};

const int = (v, fallback = 0) => {
  const n = typeof v === "string" ? parseInt(v, 10) : v;
  return Number.isFinite(n) ? n : fallback;
};

const enumOr = (v, validSet, fallback) => (validSet.has(v) ? v : fallback);
const str = (v, fallback = "") => (v == null ? fallback : String(v));

export function mapRowToDD(row) {
  if (!row) return null;
  return {
    symbol: str(row.symbol),
    calendarYear: int(row.calendarYear),
    dd_marche_status: enumOr(row.dd_marche_status, VALID_STATUS, "watch"),
    dd_marche_label: str(row.dd_marche_label, "Marché — données indisponibles"),
    dd_marche_tooltip: str(row.dd_marche_tooltip),
    dd_marche_score: num(row.dd_marche_score),
    dd_moat_status: enumOr(row.dd_moat_status, VALID_STATUS, "watch"),
    dd_moat_label: str(row.dd_moat_label),
    dd_moat_tooltip: str(row.dd_moat_tooltip),
    dd_moat_score: num(row.dd_moat_score),
    dd_value_creation_status: enumOr(row.dd_value_creation_status, VALID_STATUS, "watch"),
    dd_value_creation_label: str(row.dd_value_creation_label),
    dd_value_creation_tooltip: str(row.dd_value_creation_tooltip),
    dd_value_creation_score: num(row.dd_value_creation_score),
    dd_momentum_status: enumOr(row.dd_momentum_status, VALID_STATUS, "watch"),
    dd_momentum_label: str(row.dd_momentum_label),
    dd_momentum_tooltip: str(row.dd_momentum_tooltip),
    dd_momentum_score: num(row.dd_momentum_score),
    dd_rr_status: enumOr(row.dd_rr_status, VALID_STATUS, "watch"),
    dd_rr_label: str(row.dd_rr_label),
    dd_rr_tooltip: str(row.dd_rr_tooltip),
    dd_rr_score: num(row.dd_rr_score),
    dd_valuation_status: enumOr(row.dd_valuation_status, VALID_STATUS, "watch"),
    dd_valuation_label: str(row.dd_valuation_label),
    dd_valuation_tooltip: str(row.dd_valuation_tooltip),
    dd_valuation_score: num(row.dd_valuation_score),
    dd_global_score: num(row.dd_global_score),
    dd_global_tone: enumOr(row.dd_global_tone, VALID_TONE, "warn"),
    dd_signal: enumOr(row.dd_signal, VALID_SIGNAL, "HOLD"),
    dd_signal_tone: enumOr(row.dd_signal_tone, VALID_TONE, "warn"),
    dd_summary: str(row.dd_summary),
    dd_yes_count: int(row.dd_yes_count),
    dd_watch_count: int(row.dd_watch_count),
    dd_no_count: int(row.dd_no_count),
    radar_fondamentaux: num(row.radar_fondamentaux),
    radar_valorisation: num(row.radar_valorisation),
    radar_marche: num(row.radar_marche),
    radar_momentum: num(row.radar_momentum),
    radar_risque_rendement: num(row.radar_risque_rendement),
    iscore: num(row.iscore),
    iscore_tone: enumOr(row.iscore_tone, VALID_TONE, "warn"),
    last_updated: str(row.last_updated),
  };
}

function apiBase() {
  const isLocal =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
  return (isLocal ? "http://127.0.0.1:8000/api" : Constants.BACKEND_SERVER_BASE_URL || "").replace(/\/$/, "");
}

export async function fetchDecisionYears(symbol, opts) {
  const res = await fetch(`${apiBase()}/capitalflow/decisions?${new URLSearchParams({ symbol })}`, opts);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Erreur API (${res.status})`);
  }
  const json = await res.json();
  return Array.isArray(json.availableYears) ? json.availableYears : [];
}

export async function fetchDecisionMatrix(symbol, calendarYear, { signal } = {}) {
  if (calendarYear == null) {
    throw new Error("calendarYear requis pour fetchDecisionMatrix");
  }
  const q = new URLSearchParams({ symbol, calendarYear: String(calendarYear) });
  const url = `${apiBase()}/decisions?${q}`;
  const res = await fetch(url, { signal });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || err.detail || `Échec de récupération des décisions (${res.status}) pour ${symbol}/${calendarYear}`);
  }
  const json = await res.json();
  const row = json.row ?? json;
  const dd = mapRowToDD(row);
  if (!dd) throw new Error(`Aucune donnée de décision trouvée pour ${symbol}/${calendarYear}`);
  return dd;
}
